<template>
  <div class="tool-loader">
    <component :is="currentComponent" ref="toolComponent" :config="parsedConfig" />
    <div class="action-bar" v-if="actions.length">
      <div class="action-divider" />
      <span class="action-label">{{ $t('common.hint.quickAction') }}</span>
      <el-button
        v-for="action in actions"
        :key="action"
        size="small"
        :type="action === 'format' || action === 'copy' ? 'primary' : ''"
        :icon="icons[action]"
        :plain="action !== 'format' && action !== 'copy'"
        round
        @click="handleAction(action)"
      >
        {{ labels[action] || action }}
      </el-button>
    </div>
  </div>
</template>

<script>
import TextEditor from './TextEditor'
import JsonEditor from './JsonEditor'
import CodeEditor from './CodeEditor'
import Converter from './Converter'
import Calculator from './Calculator'
import Generator from './Generator'
import ColorPicker from './ColorPicker'
import QrcodeGenerator from './QrcodeGenerator'
import DiffViewer from './DiffViewer'
import WordToHtml from './WordToHtml'
import HtmlToWord from './HtmlToWord'
import ImageBgRemove from './ImageBgRemove'
import WordToPdf from './WordToPdf'
import PdfToWord from './PdfToWord'
import ColorExtractor from './ColorExtractor'
import IconGenerator from './IconGenerator'
import FunctionPlot from './FunctionPlot'
import CryptoTool from './CryptoTool'
import TimestampTool from './TimestampTool'
import ImageCompress from './ImageCompress'
import SqlFormatter from './SqlFormatter'
import QrDecoder from './QrDecoder'
import ImageOcr from './ImageOcr'
import PhoneList from './PhoneList'
import LocationQuery from './LocationQuery'
import CharCount from './CharCount'
import RegexTester from './RegexTester'
import JwtDecoder from './JwtDecoder'
import YamlJson from './YamlJson'
import BaseConverter from './BaseConverter'
import UuidGenerator from './UuidGenerator'
import CaseConverter from './CaseConverter'
import TextDedup from './TextDedup'
import PasswordStrength from './PasswordStrength'
import XmlFormatter from './XmlFormatter'
import HttpStatus from './HttpStatus'
import HtmlEntity from './HtmlEntity'
import UnitConverter from './UnitConverter'
import YamlProperties from './YamlProperties'
import ApiTester from './ApiTester'
import SqlQuery from './SqlQuery'
import RedisQuery from './RedisQuery'
import MarkdownEditor from './MarkdownEditor'
import MortgageCalculator from './MortgageCalculator'
import TaxCalculator from './TaxCalculator'
import WebSocketTester from './WebsocketTester'
import DnsLookup from './DnsLookup'
import SequenceDiagram from './SequenceDiagram'
import FlowChart from './FlowChart'
import MindMap from './MindMap'
import ChineseConverter from './ChineseConverter'
import BmiCalculator from './BmiCalculator'
import TableStructure from './TableStructure'
import SwaggerDoc from './SwaggerDoc'
import BabyHeight from './BabyHeight'
import BarcodeGenerator from './BarcodeGenerator'
import BarcodeDecoder from './BarcodeDecoder'
import MbtiTest from './MbtiTest'
import BloodType from './BloodType'
import ChartMaker from './ChartMaker'
import ExpressTracking from './ExpressTracking'
import CompoundInterest from './CompoundInterest'
import AsciiArt from './AsciiArt'
import PinyinConverter from './PinyinConverter'
import SslChecker from './SslChecker'
import CoordConverter from './CoordConverter'
import PptToWord from './PptToWord'
import ExcelToWord from './ExcelToWord'
import PdfSplit from './PdfSplit'
import TimezoneConverter from './TimezoneConverter'
import WatermarkRemove from './WatermarkRemove'
import CronGenerator from './CronGenerator'
import UrlCodec from './UrlCodec'
import Base64Codec from './Base64Codec'
import UnicodeCodec from './UnicodeCodec'
import GeometryDraw from './GeometryDraw'

const comps = {
  'text-editor': TextEditor, 'json-editor': JsonEditor, 'code-editor': CodeEditor,
  'converter': Converter, 'calculator': Calculator, 'generator': Generator,
  'color-picker': ColorPicker, 'qrcode-generator': QrcodeGenerator,
  'diff-viewer': DiffViewer,
  'word-to-html': WordToHtml, 'html-to-word': HtmlToWord,
  'image-bg-remove': ImageBgRemove, 'word-to-pdf': WordToPdf, 'pdf-to-word': PdfToWord,
  'color-extractor': ColorExtractor, 'icon-generator': IconGenerator,
  'function-plot': FunctionPlot,
  'crypto-tool': CryptoTool,
  'timestamp-tool': TimestampTool,
  'image-compress': ImageCompress, 'sql-formatter': SqlFormatter,
  'qr-decoder': QrDecoder,
  'image-ocr': ImageOcr,
  'phone-list': PhoneList,
  'location-query': LocationQuery,
  'char-count': CharCount,
  'regex-tester': RegexTester, 'jwt-decoder': JwtDecoder,
  'yaml-json': YamlJson, 'base-converter': BaseConverter,
  'uuid-generator': UuidGenerator, 'case-converter': CaseConverter,
  'text-dedup': TextDedup, 'password-strength': PasswordStrength,
  'xml-formatter': XmlFormatter, 'http-status': HttpStatus,
  'html-entity': HtmlEntity, 'unit-converter': UnitConverter,
  'yaml-properties': YamlProperties,
  'api-tester': ApiTester,
  'sql-query': SqlQuery,
  'redis-query': RedisQuery,
  'markdown-editor': MarkdownEditor,
  'mortgage-calculator': MortgageCalculator,
  'dns-lookup': DnsLookup,
  'tax-calculator': TaxCalculator,
  'websocket-tester': WebSocketTester,
  'flowchart': FlowChart,
  'mindmap': MindMap,
  'sequence-diagram': SequenceDiagram,
  'chinese-converter': ChineseConverter,
  'bmi-calculator': BmiCalculator,
  'table-structure': TableStructure,
  'swagger-doc': SwaggerDoc,
  'baby-height': BabyHeight,
  'barcode-generator': BarcodeGenerator,
  'barcode-decoder': BarcodeDecoder,
  'mbti-test': MbtiTest,
  'blood-type': BloodType,
  'chart-maker': ChartMaker,
  'express-tracking': ExpressTracking,
  'compound-interest': CompoundInterest,
  'ascii-art': AsciiArt,
  'pinyin-converter': PinyinConverter,
  'ssl-checker': SslChecker,
  'coord-converter': CoordConverter,
  'ppt-to-word': PptToWord,
  'excel-to-word': ExcelToWord,
  'pdf-split': PdfSplit,
  'timezone-converter': TimezoneConverter,
  'watermark-remove': WatermarkRemove,
  'cron-generator': CronGenerator,
  'url-codec': UrlCodec,
  'base64-codec': Base64Codec,
  'unicode-codec': UnicodeCodec,
  'geometry-draw': GeometryDraw
}

export default {
  name: 'ToolLoader',
  components: { TextEditor, JsonEditor, CodeEditor, Converter, Calculator, Generator, ColorPicker, QrcodeGenerator, DiffViewer, WordToHtml, HtmlToWord, ImageBgRemove, WordToPdf, PdfToWord, ColorExtractor, IconGenerator, FunctionPlot, CryptoTool, TimestampTool, ImageCompress, SqlFormatter, QrDecoder, ImageOcr, PhoneList, LocationQuery, CharCount, RegexTester, JwtDecoder, YamlJson, BaseConverter, UuidGenerator, CaseConverter, TextDedup, PasswordStrength, XmlFormatter, HttpStatus, HtmlEntity, UnitConverter, YamlProperties, ApiTester, SqlQuery, RedisQuery, MarkdownEditor, MortgageCalculator, TaxCalculator, WebSocketTester, DnsLookup, FlowChart, MindMap, SequenceDiagram, ChineseConverter, BmiCalculator, TableStructure, SwaggerDoc, BabyHeight, BarcodeGenerator, BarcodeDecoder, MbtiTest, BloodType, ChartMaker, ExpressTracking, CompoundInterest, AsciiArt, PinyinConverter, SslChecker, CoordConverter, PptToWord, ExcelToWord, WatermarkRemove, CronGenerator, UrlCodec, Base64Codec, UnicodeCodec, GeometryDraw },
  props: { tool: { type: Object, required: true } },
  data() {
    return {
      icons: {
        format: 'el-icon-s-operation', compress: 'el-icon-s-fold',
        validate: 'el-icon-circle-check', copy: 'el-icon-document-copy',
        clear: 'el-icon-delete', sample: 'el-icon-star-off',
        paste: 'el-icon-upload2', download: 'el-icon-download'
      }
    }
  },
  computed: {
    labels() {
      return {
        format: this.$t('common.action.format'), compress: this.$t('common.action.compress'),
        validate: this.$t('common.action.validate'), copy: this.$t('common.action.copy'),
        clear: this.$t('common.action.clear'), sample: this.$t('common.action.sample'),
        paste: this.$t('common.action.paste'), download: this.$t('common.action.download')
      }
    },
    currentComponent() { return comps[this.tool.componentType] || TextEditor },
    parsedConfig() {
      if (!this.tool.configJson) return {}
      try { return JSON.parse(this.tool.configJson) } catch (e) { return {} }
    },
    actions() { return this.parsedConfig.actions || [] }
  },
  methods: {
    handleAction(action) {
      const c = this.$refs.toolComponent
      if (c && c.doAction) c.doAction(action)
    }
  }
}
</script>

<style scoped>
.action-bar {
  margin-top: 24px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.action-divider {
  height: 1px; flex: 1; min-width: 20px;
  background: linear-gradient(90deg, #e4e7ed, transparent);
  order: -1;
}
.action-label {
  font-size: 12px; color: #b0b8c4; font-weight: 600;
  letter-spacing: 1.5px; text-transform: uppercase;
}
@media (max-width: 640px) { .action-divider { display: none; } }
</style>
